import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { PresetKey } from '@signalvote/shared';
import type { TenantCampaignWithCapabilitiesDto, CapabilityArea, CapabilityTier } from '@signalvote/shared';

// ============================================================================
// Types
// ============================================================================

export interface CampaignState {
  // Subsidiary info (from auth)
  subsidiaryId: string | null;
  subsidiaryCode: string | null;

  // Campaign selection
  selectedCampaign: TenantCampaignWithCapabilitiesDto | null;
  campaigns: TenantCampaignWithCapabilitiesDto[];
  isLoading: boolean;

  // Actions
  setSubsidiary: (id: string | null, code: string | null) => void;
  setCampaigns: (campaigns: TenantCampaignWithCapabilitiesDto[]) => void;
  selectCampaign: (campaign: TenantCampaignWithCapabilitiesDto | null) => void;
  setLoading: (loading: boolean) => void;
  clear: () => void;

  // Computed helpers
  hasSelectedCampaign: () => boolean;
  getSelectedCampaignId: () => string | null;
  isOwner: () => boolean;
  isFullAdmin: () => boolean;
  hasCapability: (area: CapabilityArea, minTier: CapabilityTier) => boolean;
  canView: (area: CapabilityArea) => boolean;
  canDo: (area: CapabilityArea) => boolean;
  canManage: (area: CapabilityArea) => boolean;
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'campaign-store';

// ============================================================================
// Store
// ============================================================================

export const useCampaignStore = create<CampaignState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        subsidiaryId: null,
        subsidiaryCode: null,
        selectedCampaign: null,
        campaigns: [],
        isLoading: false,

        // Actions
        setSubsidiary: (id, code) =>
          set(
            { subsidiaryId: id, subsidiaryCode: code },
            undefined,
            'setSubsidiary'
          ),

        setCampaigns: (campaigns) =>
          set((state) => {
            const updated: Partial<CampaignState> = { campaigns };
            // Sync selectedCampaign with fresh data from API. If the selected campaign is no
            // longer in the user's list (deleted / access revoked), clear it — otherwise the
            // stale id keeps going out in the X-Campaign-Id header and the API rejects every
            // subsequent request with "The specified campaign does not exist."
            if (state.selectedCampaign) {
              const fresh = campaigns.find((c) => c.id === state.selectedCampaign!.id);
              updated.selectedCampaign = fresh ?? null;
            }
            return updated;
          }, undefined, 'setCampaigns'),

        selectCampaign: (campaign) =>
          set({ selectedCampaign: campaign }, undefined, 'selectCampaign'),

        setLoading: (loading) =>
          set({ isLoading: loading }, undefined, 'setLoading'),

        clear: () =>
          set(
            {
              subsidiaryId: null,
              subsidiaryCode: null,
              selectedCampaign: null,
              campaigns: [],
              isLoading: false,
            },
            undefined,
            'clear'
          ),

        // Computed helpers
        hasSelectedCampaign: () => get().selectedCampaign !== null,

        getSelectedCampaignId: () => get().selectedCampaign?.id ?? null,

        isOwner: () => get().selectedCampaign?.isOwner === true,

        // "Full admin" for the selected campaign = owner OR the FullAdmin capability
        // preset. This mirrors the backend's FullAdminCapabilityPolicy and is the single
        // predicate the Work Orders nav item and route guard both read (ADR-068), so the
        // gate can't drift between the two call sites.
        isFullAdmin: () => {
          const campaign = get().selectedCampaign;
          if (!campaign) return false;
          return campaign.isOwner === true || campaign.presetKey === PresetKey.FullAdmin;
        },

        hasCapability: (area: CapabilityArea, minTier: CapabilityTier) => {
          const campaign = get().selectedCampaign;
          if (!campaign) return false;
          if (campaign.isOwner) return true;
          const tier = campaign.capabilities?.[area] ?? 0;
          return tier >= minTier;
        },

        canView: (area: CapabilityArea) => {
          return get().hasCapability(area, 1 as CapabilityTier); // CapabilityTier.View = 1
        },

        canDo: (area: CapabilityArea) => {
          return get().hasCapability(area, 2 as CapabilityTier); // CapabilityTier.Do = 2
        },

        canManage: (area: CapabilityArea) => {
          return get().hasCapability(area, 3 as CapabilityTier); // CapabilityTier.Manage = 3
        },
      }),
      {
        name: STORAGE_KEY,
        // Only persist campaign selection, not loading state
        partialize: (state) => ({
          subsidiaryId: state.subsidiaryId,
          subsidiaryCode: state.subsidiaryCode,
          selectedCampaign: state.selectedCampaign,
          // Note: campaigns list is not persisted - fetched fresh on login
        }),
      }
    ),
    { name: 'campaign-store' }
  )
);

// ============================================================================
// Selectors (for performance optimization)
// ============================================================================

export const selectSelectedCampaign = (state: CampaignState) => state.selectedCampaign;
